// Unpublished Work © 2021-2025 Deere & Company.

import {
    MinimumCharacterLimitForEmail, MinimumCharacterLimitForPhone,
    MinimumCharacterLengthForDBSNumber, REQUIRED_ENTITY_ID_LENGTH,
    REQUIRED_REWARDS_NUMBER_LENGTH, MaximumLengthForCRMId, MinimumRequiredCRMIdLength
} from '../state/struct/customer-search/customer-search';
import {isCitiesDropdownFacetVisible} from './default-country-helper';
import {facetKeys} from '../state/struct/customer-search/facets/selected-facets';

export const isDbsNumberValid = (dbsNumber) => [
    Boolean(dbsNumber),
    dbsNumber.length >= MinimumCharacterLengthForDBSNumber,
    /^\S+$/.test(dbsNumber)
].every((condition) => condition);

export const isRewardsNumberValid = (rewardsNumber) => {
    const formattedRewardsNumber = Number(rewardsNumber);

    return [
        !Number.isNaN(formattedRewardsNumber),
        rewardsNumber.length === REQUIRED_REWARDS_NUMBER_LENGTH
    ].every((condition) => condition);
};

export const isEntityIdValid = (entityId) => {
    const formattedEntityId = Number(entityId);

    return [
        Boolean(entityId),
        !Number.isNaN(formattedEntityId),
        entityId.length === REQUIRED_ENTITY_ID_LENGTH
    ].every((condition) => condition);
};

export const isCRMIdValid = (crmId) => [
    Boolean(crmId),
    crmId.length >= MinimumRequiredCRMIdLength,
    crmId.length <= MaximumLengthForCRMId
].every((condition) => condition);

export const isTaxIdValid = (taxId) => [
    Boolean(taxId)
].every((condition) => condition);

export const isOrgIdValid = (orgId) => {
    const formattedOrgId = Number(orgId);

    return [
        Boolean(orgId),
        !Number.isNaN(formattedOrgId),
        /^\S+$/.test(orgId)
    ].every((condition) => condition);
};

const isOptionalOptionsMet = (selectedFacets) => [
    Boolean(selectedFacets.query?.searchString),
    selectedFacets.email?.length >= MinimumCharacterLimitForEmail
].some((condition) => condition);

const hasOneOptionalCriteria = (selectedFacets) => {
    const {
        email,
        query
    } = selectedFacets;

    return [
        Boolean(email),
        Boolean(query?.searchString)
    ].some((condition) => condition);
};

const isStateCriteriaMet = (selectedFacets) => {
    const {states} = selectedFacets;

    return [
        Boolean(states?.length),
        hasOneOptionalCriteria(selectedFacets)
    ].every((condition) => condition);
};

const isPostalCriteriaMet = (selectedFacets) => {
    const {postalCode} = selectedFacets;

    return [
        Boolean(postalCode),
        hasOneOptionalCriteria(selectedFacets)
    ].every((condition) => condition);
};

const isCountryCriteriaMet = (selectedFacets) => selectedFacets.country !== '';

export const isCityCriteriaMet = (selectedFacets, facetOptions) => {
    const hasCityFacetAsDropdown = isCitiesDropdownFacetVisible(facetOptions.cityAsDropdown);

    if (hasCityFacetAsDropdown) {
        const {cities} = selectedFacets;

        return [
            Boolean(cities?.length),
            hasOneOptionalCriteria(selectedFacets)
        ].every((condition) => condition);
    }

    const {city} = selectedFacets;

    return [
        Boolean(city),
        hasOneOptionalCriteria(selectedFacets)
    ].every((condition) => condition);
};

export const hasValidCriteria = (selectedFacets, facetOptions) => [
    isCityCriteriaMet(selectedFacets, facetOptions),
    isStateCriteriaMet(selectedFacets),
    isPostalCriteriaMet(selectedFacets)
].some((condition) => condition);

const isMinimumCriteriaMet = (selectedFacets, facetOptions) => [
    hasValidCriteria(selectedFacets, facetOptions),
    isCountryCriteriaMet(selectedFacets)
].every((condition) => condition);

const isPhoneNumberValid = (selectedFacets) => {
    const {phoneNumber} = selectedFacets;

    return phoneNumber?.length >= MinimumCharacterLimitForPhone;
};

export const DEFAULT_TRUE_COUNT = 3;

export const isAllCriteriaMet = (trueCount) => trueCount === DEFAULT_TRUE_COUNT;

export const isOnlyPhoneNumberAvailable = (isPhoneNumberAvailable, trueCount) => isPhoneNumberAvailable && trueCount === 1;

export const isPhoneAndCityStateZipWithoutEmailBusinessLastName = (isPhoneNumberAvailable, isCityStateZipAvailable, isEmailBusinessLastNameAvailable) => isPhoneNumberAvailable && isCityStateZipAvailable && !isEmailBusinessLastNameAvailable;

export const isEmailBusinessLastNameAndCityStateZipWithoutPhone = (isPhoneNumberAvailable, isEmailBusinessLastNameAvailable, isCityStateZipAvailable) => !isPhoneNumberAvailable && isEmailBusinessLastNameAvailable && isCityStateZipAvailable;

export const isValidCriteria = (isCityStateZipAvailable, isPhoneNumberAvailable, isEmailBusinessLastNameAvailable) => {
    const trueCount = [isCityStateZipAvailable, isPhoneNumberAvailable, isEmailBusinessLastNameAvailable].filter(Boolean).length;

    const conditions = [
        isAllCriteriaMet(trueCount),
        isOnlyPhoneNumberAvailable(isPhoneNumberAvailable, trueCount),
        isPhoneAndCityStateZipWithoutEmailBusinessLastName(isPhoneNumberAvailable, isCityStateZipAvailable, isEmailBusinessLastNameAvailable),
        isEmailBusinessLastNameAndCityStateZipWithoutPhone(isPhoneNumberAvailable, isEmailBusinessLastNameAvailable, isCityStateZipAvailable)
    ];

    return conditions.some(Boolean);
};

export const isCombinationCriteriaMet = (selectedFacets, facetOptions) => {
    const isCityStateZipAvailable = isMinimumCriteriaMet(selectedFacets, facetOptions);
    const isPhoneNumberAvailable = isPhoneNumberValid(selectedFacets);
    const isEmailBusinessLastNameAvailable = isOptionalOptionsMet(selectedFacets);

    return isValidCriteria(isCityStateZipAvailable, isPhoneNumberAvailable, isEmailBusinessLastNameAvailable);
};

export const isValidKey = (key, selectedFacets, uniqueSearchMap) => selectedFacets[key] && uniqueSearchMap[key](selectedFacets[key]);

export const getValidUniqueKey = (selectedFacets, uniqueSearchMap) => {
    for (const key of Object.keys(uniqueSearchMap)) {
        if (isValidKey(key, selectedFacets, uniqueSearchMap)) {
            return {
                key,
                value: selectedFacets[key]
            };
        }
    }
    return null;
};

export const getCombinationCriteriaKey = (selectedFacets, facetOptions) => {
    if (isCombinationCriteriaMet(selectedFacets, facetOptions)) {
        return {
            key: 'COMBINATION_CRITERIA',
            value: null
        };
    }
    return null;
};

export const getValidFacetKey = (selectedFacets, facetOptions) => {
    const uniqueSearchMap = {
        [facetKeys.CUSTOMER_ENTITY_ID]: isEntityIdValid,
        [facetKeys.DBS_NUMBER]: isDbsNumberValid,
        [facetKeys.REWARDS_NUMBER]: isRewardsNumberValid,
        [facetKeys.TAX_ID]: isTaxIdValid,
        [facetKeys.ORG_ID]: isOrgIdValid,
        [facetKeys.CRM_ID]: isCRMIdValid
    };

    return (
        getValidUniqueKey(selectedFacets, uniqueSearchMap) ||
        getCombinationCriteriaKey(selectedFacets, facetOptions) ||
        null
    );
};
